// Split out of journey.ts (ADR-002 phase C): module state,
// late-bound actions and small shared helpers. Keeping the mutable state and
// the reload entry points here lets the render/handler modules stay acyclic:
// lower modules call trajActions.* instead of importing the loader.

import type { Journey, JourneyElement, JourneyObservation } from './journey.types';
import type { D3JourneyGraph } from './D3JourneyGraph';

export const trajState = {
  currentJourney: null as Journey | null,
  selectedObservationId: null as string | null,
  allJourneys: [] as Journey[],
  currentViewMode: 'my' as 'my' | 'shared',
  currentViewTab: 'mycelium' as 'columns' | 'mycelium',
  d3Graph: null as D3JourneyGraph | null,
  onboardingEnabled: true,
};

export const stageConfig = {
  STAGE_NAMES: [
    '0. Фиксация ситуации',
    '1. Первичная реакция (эго)',
    '2. Снятие проекций',
    '3. Анализ искажений (анализ)',
    '4. Выявление глубинного импульса',
    '5. Формирование нового действия',
  ],
  STAGE_DESCS: [
    'Описание объективного факта без интерпретаций.',
    'Что вы почувствовали, подумали или захотели сделать сразу.',
    'Где в этой ситуации ваши собственные нерешенные конфликты.',
    'Запустите анализ системы для поиска эго-искажений.',
    'Какое истинное стремление стояло за этой реакцией.',
    'Какое чистое действие необходимо предпринять из реальности.',
  ],
  QUALITATIVE_STAGE_NAMES: [
    '0. Исходное состояние',
    '1. Выявление ловушки',
    '2. Анализ (ЭР)',
    '3. Преодоление',
    '4. Стабилизация',
    '5. Проверка в Общности',
    '6. Общность в действии',
  ],
  QUALITATIVE_STAGE_DESCS: [
    'Фиксация жалобы без анализа.',
    'Обнаружение повторяющегося шаблона.',
    'Сопоставление с концептами ЭР.',
    'Практика в моменте.',
    'Закрепление паттерна.',
    'Верификация через поле Общности.',
    'Фиксация участия в поле Общности без эго-фильтров; не статус и не достижение.',
  ],
};

// Late-bound reload entry points, registered by journey.ts (the feature
// entry that owns the loaders). Render/handler modules call these instead of
// importing the loader module - that import would be circular.
export const trajActions = {
  reloadJourneys: async (): Promise<void> => {},
  reloadDetails: async (_id: string): Promise<void> => {},
};

export function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/"/g, '&quot;');
}

export function isJourneyReadOnly(journey: Journey): boolean {
  return !!journey.is_demo;
}

export function findObservationById(obsId: string): { obs: JourneyObservation; element: JourneyElement } | null {
  if (!trajState.currentJourney || !trajState.currentJourney.elements) return null;
  for (const el of trajState.currentJourney.elements) {
    if (!el.observations) continue;
    const obs = el.observations.find(o => o.id === obsId);
    if (obs) return { obs, element: el };
  }
  return null;
}

export function mergeObservationIntoJourney(obs: JourneyObservation) {
  if (!trajState.currentJourney?.elements) return;
  for (const el of trajState.currentJourney.elements) {
    if (!el.observations) continue;
    const idx = el.observations.findIndex((o) => o.id === obs.id);
    if (idx >= 0) {
      el.observations[idx] = { ...el.observations[idx], ...obs };
      return;
    }
  }
}

export function escapeHtml(value: string) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
